/**
 * Marketplace Federation — the revocable bridge between two Root Entities.
 *
 * See docs/federation-backend.md and the Strategy & Architecture document
 * (Part II §7 and Part III §10) for the full design. The frontend types
 * mirror what the backend will return — until those endpoints ship, the
 * frontend uses the canonical apiClient and the backend can return 404s.
 *
 * The federation lifecycle has six stages. The state machine is encoded
 * here so every page that branches on state reads from one source.
 *
 *   discovery
 *     ↓
 *   requested      ← Service Provider Request (SPR) drafted and sent
 *     ↓                          ↘
 *   accepted ────────────────── rejected   (terminal)
 *     ↓
 *   active         ← engagement runs, agents execute, evidence accumulates
 *     ↓                          ↘
 *   renewed → active             revoked
 *                                  ↓
 *                                cleaned_up  (terminal — 7d after revoked)
 */

export type FederationState =
  | 'discovery'
  | 'requested'
  | 'accepted'
  | 'active'
  | 'renewed'
  | 'rejected'
  | 'revoked'
  | 'cleaned_up';

export const FEDERATION_STATES: FederationState[] = [
  'discovery',
  'requested',
  'accepted',
  'active',
  'renewed',
  'rejected',
  'revoked',
  'cleaned_up',
];

export const FEDERATION_STATE_LABEL: Record<FederationState, string> = {
  discovery: 'Discovery',
  requested: 'Requested',
  accepted: 'Accepted',
  active: 'Active',
  renewed: 'Renewed',
  rejected: 'Rejected',
  revoked: 'Revoked',
  cleaned_up: 'Cleaned up',
};

export const FEDERATION_STATE_TINT: Record<FederationState, string> = {
  discovery: 'bg-slate-100 text-slate-700 ring-slate-200',
  requested: 'bg-amber-100 text-amber-800 ring-amber-200',
  accepted: 'bg-blue-100 text-blue-800 ring-blue-200',
  active: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  renewed: 'bg-teal-100 text-teal-800 ring-teal-200',
  rejected: 'bg-rose-100 text-rose-800 ring-rose-200',
  revoked: 'bg-rose-100 text-rose-800 ring-rose-200',
  cleaned_up: 'bg-slate-100 text-slate-500 ring-slate-200',
};

export const FEDERATION_STATE_DOT: Record<FederationState, string> = {
  discovery: 'bg-slate-400',
  requested: 'bg-amber-500',
  accepted: 'bg-blue-500',
  active: 'bg-emerald-500',
  renewed: 'bg-teal-500',
  rejected: 'bg-rose-500',
  revoked: 'bg-rose-500',
  cleaned_up: 'bg-slate-400',
};

export const ACTIVE_LIKE_STATES: FederationState[] = ['accepted', 'active', 'renewed'];
export const TERMINAL_STATES: FederationState[] = ['rejected', 'cleaned_up'];

export function isActiveLike(state: FederationState): boolean {
  return ACTIVE_LIKE_STATES.includes(state);
}

export function isTerminal(state: FederationState): boolean {
  return TERMINAL_STATES.includes(state);
}

/**
 * Legal transitions out of each state. Used by FederationDetail to know
 * which action buttons to show and by the backend to validate PATCH calls.
 */
export const LEGAL_TRANSITIONS: Record<FederationState, FederationState[]> = {
  discovery: ['requested'],
  requested: ['accepted', 'rejected'],
  accepted: ['active', 'revoked'],
  active: ['renewed', 'revoked'],
  renewed: ['revoked'],
  rejected: [],
  revoked: ['cleaned_up'],
  cleaned_up: [],
};

export function canTransition(from: FederationState, to: FederationState): boolean {
  return LEGAL_TRANSITIONS[from].includes(to);
}

/**
 * Guest roles that a federated party gets inside the counterparty's
 * workspace. The federation key encodes the role; the JWT carries the
 * federation_id when the guest acts.
 */
export type GuestRole = 'guest_architect' | 'guest_practitioner' | 'guest_auditor';

export const GUEST_ROLES: GuestRole[] = [
  'guest_architect',
  'guest_practitioner',
  'guest_auditor',
];

export const GUEST_ROLE_LABEL: Record<GuestRole, string> = {
  guest_architect: 'Guest Architect',
  guest_practitioner: 'Guest Practitioner',
  guest_auditor: 'Guest Auditor',
};

export const GUEST_ROLE_DESCRIPTION: Record<GuestRole, string> = {
  guest_architect:
    'Lead authority inside the federation. Can deploy playbooks, configure agent crews, sign regulator-bound deliverables.',
  guest_practitioner:
    'Executes tasks within the engagement. Proposes evidence, receives agent output for review. Cannot bypass HITL.',
  guest_auditor:
    'Independent assurance role. Read-only access to engagement evidence under a tamper-evident workspace.',
};

/** LLM tier elected for the federation (mirrors the per-tenant tier). */
export type LlmTier = 'sovereign' | 'vendor_gateway' | 'byom';

export const LLM_TIER_LABEL: Record<LlmTier, string> = {
  sovereign: 'Comply.Now Sovereign Cloud',
  vendor_gateway: 'Vendor Gateway (BYOK)',
  byom: 'Bring Your Own Model',
};

export const LLM_TIER_DESCRIPTION: Record<LlmTier, string> = {
  sovereign:
    'Open-weights models hosted by Comply.Now in regional sovereign clusters. Metered token usage.',
  vendor_gateway:
    'Tenant brings their own enterprise key for Bedrock / Azure / Groq / Anthropic. Compute pass-through.',
  byom:
    'Tenant runs their own Llama on-premise (Ollama, vLLM, NIM). No data leaves the perimeter.',
};

/**
 * Federation scope — what the consultant proposes and the client accepts.
 * Drafted in the SPR, frozen on acceptance, audited on every change.
 */
export interface FederationScope {
  /** Free-text scope description. */
  description: string;
  /** Playbooks (by id) that the consultant deploys into the client workspace. */
  playbookIds: string[];
  /** Guest roles granted to the consultant's practitioners. */
  guestRoles: GuestRole[];
  /** L2 subsidiaries inside the client whose data is in scope. Empty = root only. */
  subsidiaryTenantIds: string[];
  /** Months the federation is contracted for. */
  durationMonths: number;
  /** LLM tier elected for this federation. Overrides per-tenant default. */
  llmTier: LlmTier;
  /** PII redaction required before egress, regardless of LLM tier. */
  piiRedactionRequired: boolean;
}

export interface Federation {
  id: string;
  /** Originator — almost always a consulting_firm or audit_firm. */
  fromTenantId: string;
  fromTenantName: string;
  /** Receiver — almost always a client. */
  toTenantId: string;
  toTenantName: string;
  state: FederationState;
  scope: FederationScope;
  /** Federation key — issued on acceptance, encodes Guest role. */
  federationKey: string | null;
  createdAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  cleanupCompletedAt: string | null;
  /** Partner attribution ledger entry id if this federation originated a new client. */
  attributionId: string | null;
}

export type FederationEventType =
  | 'created'
  | 'spr_proposed'
  | 'spr_accepted'
  | 'spr_rejected'
  | 'activated'
  | 'renewed'
  | 'scope_changed'
  | 'playbook_deployed'
  | 'playbook_removed'
  | 'revoked'
  | 'cleanup_scheduled'
  | 'cleanup_completed';

export interface FederationAuditEvent {
  id: string;
  federationId: string;
  type: FederationEventType;
  actorUserId: string;
  actorTenantId: string;
  /** Free-text or structured payload, depending on event type. */
  details: Record<string, unknown>;
  createdAt: string;
}

/**
 * Default scope used when seeding a new SPR. Sovereign Cloud + PII redaction
 * on is the conservative default per the doc's MENA banking guidance.
 */
export const DEFAULT_FEDERATION_SCOPE: FederationScope = {
  description: '',
  playbookIds: [],
  guestRoles: ['guest_architect'],
  subsidiaryTenantIds: [],
  durationMonths: 6,
  llmTier: 'sovereign',
  piiRedactionRequired: true,
};
